import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";
import Label from "src/components/Label";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import TrackerDetailOverviewTab from "./TrackerDetailOverviewTab";
import TrackerDetailInfoTab from "./TrackerDetailInfoTab";
import TrackerDetailPeopleTab from "./TrackerDetailPeopleTab";
import TrackerDetailSubmissionsTab from "./TrackerDetailSubmissionsTab";
import TrackerDetailAutomationsTab from "./TrackerDetailAutomationsTab";
import {
  getTrackerDetailApi,
  duplicateTrackerApi,
  archiveTrackerApi,
  deleteTrackerApi,
} from "src/DAL/tracker/tracker";
import {
  archiveTracker,
  cacheTrackersFromApi,
  deleteTracker,
  ensureTrackerShape,
  getTrackerById,
  isLeadCaptureTracker,
  normalizeTracker,
  TRACKER_STATUS,
} from "./trackerStorage";

const PRIMARY = "var(--portal-theme-primary)";
const PRIMARY_SOFT =
  "color-mix(in srgb, var(--portal-theme-primary) 12%, white)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const JOURNEY_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "People" },
  { id: "submissions", label: "Submissions" },
  { id: "automations", label: "Automations" },
];

const LEAD_DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "details", label: "Tracker details" },
  { id: "people", label: "Leads" },
  { id: "submissions", label: "Submissions" },
  { id: "automations", label: "Automations" },
];

export default function TrackerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [tracker, setTracker] = useState(() => {
    const cached = getTrackerById(id);
    return cached ? ensureTrackerShape(cached) : null;
  });
  const [loading, setLoading] = useState(!tracker);
  const [openDelete, setOpenDelete] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const actionsOpen = Boolean(actionsAnchor);

  const tabParam = searchParams.get("tab") || "overview";
  const isLead = isLeadCaptureTracker(tracker);
  const DETAIL_TABS = isLead ? LEAD_DETAIL_TABS : JOURNEY_DETAIL_TABS;
  const activeTab = DETAIL_TABS.some((t) => t.id === tabParam)
    ? tabParam
    : "overview";

  const setActiveTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    if (nextTab === "overview") next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  const closeActionsMenu = () => setActionsAnchor(null);

  const fetchTrackerDetail = useCallback(async () => {
    const looksLikeMongoId = /^[a-f\d]{24}$/i.test(String(id || ""));
    try {
      const res = await getTrackerDetailApi(id);
      if (res?.code === 200 && res.tracker) {
        const normalized = normalizeTracker(res.tracker);
        const shaped = ensureTrackerShape(normalized);
        setTracker(shaped);
        cacheTrackersFromApi([normalized]);
        return;
      }
      if (res?.message) {
        enqueueSnackbar(res.message, { variant: "warning" });
      }
    } catch (e) {
      console.warn("Tracker detail API error", e);
    } finally {
      setLoading(false);
    }

    // Only use local cache for real API ids (never demo / local-only drafts).
    const cached =
      looksLikeMongoId ? ensureTrackerShape(getTrackerById(id)) : null;
    if (!cached) {
      enqueueSnackbar("Tracker not found", { variant: "error" });
      navigate("/trackers", { replace: true });
    } else {
      setTracker(cached);
    }
  }, [id, enqueueSnackbar, navigate]);

  useEffect(() => {
    fetchTrackerDetail();
  }, [fetchTrackerDetail]);

  const statusLabel = useMemo(() => {
    if (!tracker) return "Draft";
    if (
      tracker.status === TRACKER_STATUS.LIVE ||
      tracker.status === "published"
    ) {
      return "Live";
    }
    if (tracker.status === TRACKER_STATUS.ARCHIVED) return "Archived";
    return "Draft";
  }, [tracker]);

  const statusColor = useMemo(() => {
    if (statusLabel === "Live") return "success";
    if (statusLabel === "Archived") return "default";
    return "warning";
  }, [statusLabel]);

  const editHref = `/trackers/edit/${tracker?.id || tracker?._id || id}`;

  const handleDuplicate = async () => {
    closeActionsMenu();
    try {
      const res = await duplicateTrackerApi(tracker.id || tracker._id);
      if (res?.code === 200 && res.tracker) {
        const copy = normalizeTracker(res.tracker);
        cacheTrackersFromApi([copy]);
        enqueueSnackbar(
          isLeadCaptureTracker(copy)
            ? "Lead capture duplicated"
            : "Journey duplicated",
          { variant: "success" }
        );
        navigate(`/trackers/${copy.id}`);
        return;
      }
      enqueueSnackbar(res?.message || "Could not duplicate tracker", {
        variant: "error",
      });
    } catch (e) {
      enqueueSnackbar("Could not duplicate tracker", { variant: "error" });
    }
  };

  const handleArchive = async () => {
    closeActionsMenu();
    if (tracker.status === TRACKER_STATUS.ARCHIVED) {
      enqueueSnackbar("Already archived", { variant: "info" });
      return;
    }
    try {
      const res = await archiveTrackerApi(tracker.id || tracker._id);
      if (res?.code === 200 && res.tracker) {
        const updated = normalizeTracker(res.tracker);
        const shaped = ensureTrackerShape(updated);
        setTracker(shaped);
        cacheTrackersFromApi([updated]);
        enqueueSnackbar("Journey archived — intake stopped", {
          variant: "success",
        });
        return;
      }
      enqueueSnackbar(res?.message || "Could not archive tracker", {
        variant: "error",
      });
    } catch (e) {
      enqueueSnackbar("Could not archive tracker", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteTrackerApi(tracker.id || tracker._id);
      if (res?.code !== 200) {
        enqueueSnackbar(res?.message || "Could not delete tracker", {
          variant: "error",
        });
        return;
      }
    } catch (e) {
      enqueueSnackbar("Could not delete tracker", { variant: "error" });
      return;
    }
    deleteTracker(tracker.id);
    setOpenDelete(false);
    enqueueSnackbar("Tracker deleted", { variant: "success" });
    navigate("/trackers");
  };

  if (loading && !tracker) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={36} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!tracker) return null;

  return (
    <>
      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Are you sure you want to delete this Tracker?"
        handleAgree={handleDelete}
      />

      <div className="container-fluid tracker-module-page">
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <IconButton
                size="small"
                onClick={() => navigate("/trackers")}
                sx={{ p: 0.75 }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                type="button"
                onClick={() => navigate("/trackers")}
                sx={{
                  border: 0,
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  color: MUTED,
                  fontSize: 13,
                  "&:hover": { color: PRIMARY },
                }}
              >
                All trackers
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>/</Typography>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 600,
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {tracker.name ||
                  (isLead ? "Untitled lead capture" : "Untitled journey")}
              </Typography>
              <Label variant="ghost" color={statusColor}>
                {statusLabel}
              </Label>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 24, md: 28 },
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              {tracker.name ||
                (isLead ? "Untitled lead capture" : "Untitled journey")}
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: 13.5, color: MUTED }}>
              {isLead ? "Lead Capture · Standard Tracker" : "Journey Tracker"}
              {tracker.updatedAt
                ? ` · updated ${moment(tracker.updatedAt).format(
                    "DD MMM YYYY HH:mm"
                  )}`
                : ""}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              className="small-contained-button"
              onClick={() => navigate(editHref)}
            >
              <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Edit
            </button>
            <IconButton
              size="small"
              aria-label="More actions"
              aria-controls={actionsOpen ? "tracker-detail-actions" : undefined}
              aria-haspopup="true"
              aria-expanded={actionsOpen ? "true" : undefined}
              onClick={(e) => setActionsAnchor(e.currentTarget)}
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                bgcolor: "#fff",
                "&:hover": { bgcolor: PRIMARY_SOFT, borderColor: PRIMARY },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              id="tracker-detail-actions"
              anchorEl={actionsAnchor}
              open={actionsOpen}
              onClose={closeActionsMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 0.75,
                  minWidth: 180,
                  borderRadius: "10px",
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  setActiveTab("submissions");
                }}
              >
                <ListItemIcon>
                  <ListAltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Submissions</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  handleDuplicate();
                }}
              >
                <ListItemIcon>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={tracker.status === TRACKER_STATUS.ARCHIVED}
                onClick={() => {
                  closeActionsMenu();
                  handleArchive();
                }}
              >
                <ListItemIcon>
                  <ArchiveOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Archive</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeActionsMenu();
                  setOpenDelete(true);
                }}
                sx={{ color: "#dc2626" }}
              >
                <ListItemIcon sx={{ color: "#dc2626" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            mb: 2.5,
            pb: 1.5,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          {DETAIL_TABS.map((t) => (
            <Chip
              key={t.id}
              label={t.label}
              onClick={() => setActiveTab(t.id)}
              sx={{
                fontWeight: 700,
                cursor: "pointer",
                bgcolor: activeTab === t.id ? PRIMARY : "#fff",
                color: activeTab === t.id ? "#fff" : "#0f172a",
                border: `1px solid ${activeTab === t.id ? PRIMARY : BORDER}`,
                "&:hover": {
                  bgcolor: activeTab === t.id ? PRIMARY : PRIMARY_SOFT,
                },
              }}
            />
          ))}
        </Box>

        {activeTab === "overview" ? (
          <TrackerDetailOverviewTab tracker={tracker} />
        ) : null}
        {activeTab === "details" ? (
          <TrackerDetailInfoTab tracker={tracker} />
        ) : null}
        {activeTab === "people" ? (
          <TrackerDetailPeopleTab tracker={tracker} />
        ) : null}
        {activeTab === "submissions" ? (
          <TrackerDetailSubmissionsTab tracker={tracker} />
        ) : null}
        {activeTab === "automations" ? (
          <TrackerDetailAutomationsTab tracker={tracker} />
        ) : null}
      </div>
    </>
  );
}
